import React from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@emotion/react";
import {useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {flexAlignCenter, mb, mr, onDesktop, p, textAlign} from "@pg-design/helpers-css";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {truncate} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {SchemaArticleAuthorDetail} from "../../schemas/SchemaArticleAuthorDetail";

interface IProps {
    author: {
        image: {a_img_130x130?: string};
    };
}

export const AuthorDescription = (props: IProps) => {
    const theme = useTheme();
    const authorName = useSelector((state: IRPStore) => state.article.detail.article?.author.name);

    const description = useSelector((state: IRPStore) => state.article.detail.article?.author.description);
    const locationPathname = useLocation().pathname;
    const authorImageUrl = useSelector((state: IRPStore) => state.article.detail.article?.author.image?.a_img_130x130);
    const id = useSelector((state: IRPStore) => state.article.detail.article?.author.id);
    const slug = useSelector((state: IRPStore) => state.article.detail.article?.author.slug);
    const link = id && slug ? rpAppLink.article.author.detail({authorId: id, authorSlug: slug}) : undefined;
    const isFeatured = useSelector((state: IRPStore) => state.article.detail.article?.author.is_featured);

    const authorDescHandler = () => {
        window.open(link, "_blank");
    };
    return (
        <AuthorDescriptionBase onClick={authorDescHandler}>
            <Image width="130px" height="130px" css={authorImg} src={props.author.image.a_img_130x130} alt={authorName ?? ""} />

            <div>
                <Text align="inherit" as="div" variant="headline_5" css={mb(1.5)}>
                    {isFeatured ? (
                        <a href={link} target="_blank">
                            <Highlight>{authorName}</Highlight>
                        </a>
                    ) : (
                        <Highlight>{authorName}</Highlight>
                    )}
                </Text>
                {description ? (
                    <Text align="inherit" color={theme.colors.gray[800]} variant="body_copy_2">
                        {truncateToSentence(description, 400)}
                    </Text>
                ) : null}
            </div>

            <SchemaArticleAuthorDetail name={authorName} description={description} pathname={locationPathname} authorImageUrl={authorImageUrl} />
        </AuthorDescriptionBase>
    );
};

export const truncateToSentence = (value: string, length: number) => {
    const resultToLatSentence = truncate(value, {
        length: length,
        separator: /(\. )+/,
        omission: "."
    });

    if (resultToLatSentence.length > 0) {
        return resultToLatSentence;
    }

    return truncate(value, {
        length: length,
        separator: /,? +/,
        omission: String.fromCharCode(8230)
    });
};

const authorImg = css`
    flex-grow: 0;
    flex-shrink: 0;
    ${mb(2)};

    ${onDesktop(mr(2))};
`;

const AuthorDescriptionBase = styled.div`
    display: block;
    ${p(1.5)};
    ${textAlign("center")};
    ${mb(4)};

    ${onDesktop(mb(3), flexAlignCenter, textAlign("left"))};
    &:hover {
        cursor: pointer;
    }
`;
